import React from "react";
import clsx from "clsx";
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  useTheme,
  darken,
  BoxProps,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ChevronDownIcon as CaretDownIcon } from "lucide-react";
import { Link } from "../link/Link";
import { useLocation, useNavigate } from "react-router-dom";
import { useDrawer } from "../../hooks/useDrawer";
import { getLocalStorageMinimizedDrawerValue } from "../../contexts/MinimizedDrawerContext";
import { UserRole, LocalStorageKeys } from "../../constants";
import { useCurrentUser } from "../../hooks/useCurrentUser";
import { sharedLabelAnimationSx } from "../app/AppDrawer";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 50,
    position: "sticky",
    top: theme.spacing(-1),
    background: "white",
    zIndex: theme.zIndex.mobileStepper,
    "&:hover": {
      background: darken(theme.palette.common.white, 0.05),
    },
  },
  active: {
    "& $icon, & $text": {
      color: theme.palette.primary.main,
    },
  },
  icon: {
    color: theme.palette.text.secondary,
  },
  text: {
    color: theme.palette.text.secondary,
    fontSize: 19,
    display: "flex",
    alignItems: "center",
  },
  caret: {
    marginLeft: theme.spacing(1),
    width: theme.spacing(2.75),
    height: theme.spacing(2.75),
    marginTop: theme.spacing(0.1),
    "$expanded &": {
      transform: "rotate(180deg)",
    },
  },
  expanded: {},
}));

export interface ExpandableNavItemProps extends BoxProps {
  icon: React.ReactNode;
  label: string;
  totalCount: number;
  minimized?: boolean;
  setDrawerMinimized?: React.Dispatch<React.SetStateAction<boolean>>;
  active: boolean;
  coachRedirectPath: string;
  children: React.ReactNode;
}

export function ExpandableNavItem({
  icon,
  label,
  className,
  totalCount,
  minimized,
  setDrawerMinimized,
  active,
  coachRedirectPath,
  children,
}: ExpandableNavItemProps) {
  const s = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [, setDrawerOpen] = useDrawer();
  const user = useCurrentUser();

  const [expanded, setExpanded] = React.useState(active);

  const handleToggle = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((val) => !val);
  }, []);

  const handleClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      if (user?.role === UserRole.COACH) {
        navigate(coachRedirectPath);
        setDrawerOpen(false);
      } else {
        handleToggle(e);
        setDrawerMinimized?.(false);
        localStorage.setItem(LocalStorageKeys.IS_DRAWER_MINIMIZED, "false");
      }
    },
    [handleToggle, user?.role],
  );

  React.useEffect(() => {
    if (active) setExpanded(true);
  }, [active]);

  React.useEffect(() => {
    minimized && setExpanded(false);
  }, [minimized]);

  React.useEffect(() => {
    if (!active && minimized) setExpanded(false);
    setDrawerMinimized?.(getLocalStorageMinimizedDrawerValue());
  }, []);

  const open = !(minimized || !expanded);

  return (
    (user?.role === UserRole.COACH || totalCount > 0) && (
      <>
        <ListItemButton
          className={clsx(
            s.root,
            active && s.active,
            expanded && s.expanded,
            className,
          )}
          component={Link}
          underline="none"
          onClick={handleClick}
          sx={{
            ...(!minimized && { p: 0, paddingLeft: theme.spacing(4) }),
            transition: theme.transitions.create(["padding-left"], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <ListItemIcon sx={{ minWidth: minimized ? 30 : 40 }}>
            <span className={clsx(s.icon)}>{icon}</span>
          </ListItemIcon>
          <ListItemText
            disableTypography
            sx={{ ...sharedLabelAnimationSx(minimized) }}
          >
            <Typography variant="h5" className={s.text}>
              {label}
              {totalCount > 0 && (
                <CaretDownIcon className={s.caret} onClick={handleToggle} />
              )}
            </Typography>
          </ListItemText>
        </ListItemButton>
        <Collapse in={open} timeout="auto">
          {children}
        </Collapse>
      </>
    )
  );
}
